import { Fragment, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    FiCheckCircle,
    FiChevronRight,
    FiDownload,
    FiEdit3,
    FiMaximize2,
    FiSave,
    FiTarget,
    FiX,
} from "react-icons/fi";
import ActionButton from "./ActionButton";
import { FormCard } from "./FormCard";
import StageStepsBar from "./StageStepsBar";
import StageStepHeader from "./StageStepHeader";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { generateSmile } from "../../../store/smileDesign/smileGenerationSlice";
import {
    DENTAL_ACCURACY_REFINE_LABEL,
    DENTAL_ACCURACY_REFINE_PROMPT,
} from "../../../utils/smileDesign/dentalAccuracyOutlinePrompt";
import {
    traceSmileWireframe,
    buildWireTeethFromContours,
    compositeEditedAfter,
    setWireToothTransform,
    type WireTeethModel,
} from "../../../utils/smileDesign/dentalAccuracy";
import AfterToothEditView from "../dentalAccuracy/AfterToothEditView";
import SmileSuperimposeViewer from "../dentalAccuracy/SmileSuperimposeViewer";
import GenerationLoader from "./GenerationLoader";

interface Props {
    imageUrl: string | null;
    smileType?: string;
    patientName: string;
    /** Stage 1 before photo (data URL / blob URL) */
    beforePreview: string | null;
    /** Stage 1 before File when available */
    beforeFile: File | null;
    /** Wireframe mapped to the selected after */
    wireframeUrl?: string | null;
    wireframeSaved?: boolean;
    /** Edited before+wireframe composite mapped to the selected after */
    overlayUrl?: string | null;
    overlaySaved?: boolean;
    /** AI restore with teeth extended to wireframe borders */
    restoredUrl?: string | null;
    restoredSaved?: boolean;
    onComplete: () => void;
    /** Attach a wireframe traced from this after's teeth */
    onWireframeTraced?: (afterImageUrl: string, wireframeDataUrl: string) => void;
    /** Remember which after the next extend restore should attach to */
    onExtendGenerateStart?: (afterImageUrl: string) => void;
    /** Save unsaved wireframes (+ overlays + restores) for all afters */
    onSave?: () => void;
    /** Save the exact edited preview composite linked to this after */
    onSaveEditedOverlay?: (dataUrl: string) => void | Promise<void | boolean>;
    /** Unsaved edited-after composite, so the case form can offer it */
    onLiveEditedAfter?: (url: string | null) => void;
    smileId?: string;
    saving?: boolean;
    canSave?: boolean;
    /** Case ready to accept overlay upload */
    canSaveOverlay?: boolean;
    /** True when this after's wireframe is already saved */
    selectedSaved?: boolean;
}

function cloneTeeth(model: WireTeethModel): WireTeethModel {
    return {
        ...model,
        teeth: model.teeth.map((t) => ({
            ...t,
            base: t.base.map((p) => ({ ...p })),
            cum: t.cum.slice(),
            handles: t.handles.map((h) => ({ ...h })),
            scaleX: t.scaleX,
            scaleY: t.scaleY,
            shiftX: t.shiftX,
            shiftY: t.shiftY,
        })),
    };
}

async function urlToImageFile(url: string, filename: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();
    const type = blob.type || "image/png";
    const ext = type.includes("jpeg") || type.includes("jpg") ? "jpg" : "png";
    const name = /\.(jpe?g|png|webp)$/i.test(filename)
        ? filename
        : `${filename}.${ext}`;
    return new File([blob], name, { type });
}

export default function DentalAccuracyStage({
    imageUrl,
    smileType,
    patientName,
    beforePreview,
    wireframeUrl = null,
    wireframeSaved = false,
    overlayUrl = null,
    overlaySaved = false,
    restoredUrl = null,
    restoredSaved = false,
    onComplete,
    onExtendGenerateStart,
    onSave,
    onSaveEditedOverlay,
    onLiveEditedAfter,
    smileId,
    saving = false,
    canSave = false,
    canSaveOverlay = false,
    selectedSaved = false,
}: Props) {
    const dispatch = useAppDispatch();
    const user = useAppSelector((s) => s.auth.user);
    const { loading: aiLoading, error: outlineError } = useAppSelector(
        (s) => s.smileGeneration
    );

    const [localError, setLocalError] = useState<string | null>(null);
    const [aiAction, setAiAction] = useState<"refine" | null>(null);
    const [compositeBusy, setCompositeBusy] = useState(false);
    /** Fullscreen view of the already-saved edited on-before composite */
    const [overlayViewOpen, setOverlayViewOpen] = useState(false);
    /** Fullscreen view of the AI-refined smile */
    const [restoredViewOpen, setRestoredViewOpen] = useState(false);
    const [afterModel, setAfterModel] = useState<WireTeethModel | null>(null);
    const [afterOriginal, setAfterOriginal] = useState<WireTeethModel | null>(null);
    const [afterEdited, setAfterEdited] = useState(false);
    const [afterEditOpen, setAfterEditOpen] = useState(false);
    const [afterBusy, setAfterBusy] = useState(false);
    const [afterSelectedId, setAfterSelectedId] = useState<string | null>(null);
    const [afterSelectedIds, setAfterSelectedIds] = useState<string[]>([]);
    const [afterSelectedHandle, setAfterSelectedHandle] = useState<number | null>(
        null
    );
    const [editedAfterUrl, setEditedAfterUrl] = useState<string | null>(null);
    const [editedAfterViewOpen, setEditedAfterViewOpen] = useState(false);

    const resetAfterTeeth = useCallback(() => {
        setAfterModel(null);
        setAfterOriginal(null);
        setAfterEdited(false);
        setAfterEditOpen(false);
        setAfterSelectedId(null);
        setAfterSelectedIds([]);
        setAfterSelectedHandle(null);
        setEditedAfterUrl(null);
        setEditedAfterViewOpen(false);
    }, []);

    // Drop the editor when switching the source after smile
    useEffect(() => {
        resetAfterTeeth();
        setOverlayViewOpen(false);
        setRestoredViewOpen(false);
        setEditedAfterViewOpen(false);
        setAiAction(null);
    }, [imageUrl, resetAfterTeeth]);

    // Bake the live after (tooth sizes applied) so View / Download / Refine are ready
    useEffect(() => {
        if (!imageUrl || !afterModel) {
            setEditedAfterUrl(null);
            return;
        }
        const timer = window.setTimeout(() => {
            void compositeEditedAfter(imageUrl, afterModel)
                .then(setEditedAfterUrl)
                .catch(() => setEditedAfterUrl(null));
        }, afterEdited ? 280 : 0);
        return () => window.clearTimeout(timer);
    }, [imageUrl, afterModel, afterEdited]);

    useEffect(() => {
        onLiveEditedAfter?.(overlayUrl ? null : editedAfterUrl);
    }, [editedAfterUrl, overlayUrl, onLiveEditedAfter]);

    // Close overlay viewer on Escape / when the overlay is no longer available
    useEffect(() => {
        if (!overlayViewOpen) return;
        if (!overlayUrl) {
            setOverlayViewOpen(false);
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOverlayViewOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [overlayViewOpen, overlayUrl]);

    useEffect(() => {
        if (!editedAfterViewOpen) return;
        if (!editedAfterUrl) {
            setEditedAfterViewOpen(false);
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setEditedAfterViewOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [editedAfterViewOpen, editedAfterUrl]);

    // Close restored viewer on Escape / when the restore is no longer available
    useEffect(() => {
        if (!restoredViewOpen) return;
        if (!restoredUrl) {
            setRestoredViewOpen(false);
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setRestoredViewOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [restoredViewOpen, restoredUrl]);

    // Clear local AI action when Redux loading settles
    useEffect(() => {
        if (!aiLoading) setAiAction(null);
    }, [aiLoading]);

    const handleEditOnAfter = useCallback(async () => {
        if (!imageUrl) return;
        if (afterModel) {
            setAfterEditOpen(true);
            return;
        }
        setLocalError(null);
        setAfterBusy(true);
        try {
            const result = await traceSmileWireframe(imageUrl);
            const model = buildWireTeethFromContours(
                result.sourceContours,
                result.imageWidth,
                result.imageHeight
            );
            if (model.teeth.length < 4) {
                throw new Error("Could not find enough teeth on this after image");
            }
            setAfterOriginal(cloneTeeth(model));
            setAfterModel(model);
            setAfterEdited(false);
            setAfterSelectedId(null);
            setAfterSelectedIds([]);
            setAfterSelectedHandle(null);
            setAfterEditOpen(true);
            onComplete();
        } catch (err: unknown) {
            const responseMessage =
                err &&
                typeof err === "object" &&
                "response" in err
                    ? (err as { response?: { data?: { message?: string } } })
                          .response?.data?.message
                    : undefined;
            const message =
                responseMessage ||
                (err instanceof Error ? err.message : "Tooth tracing failed");
            setLocalError(message);
        } finally {
            setAfterBusy(false);
        }
    }, [imageUrl, afterModel, onComplete]);

    const bakeEditedAfter = useCallback(async (): Promise<string> => {
        if (editedAfterUrl) return editedAfterUrl;
        if (!imageUrl || !afterModel) {
            throw new Error("Edit the after image first.");
        }
        const dataUrl = await compositeEditedAfter(imageUrl, afterModel);
        setEditedAfterUrl(dataUrl);
        return dataUrl;
    }, [editedAfterUrl, imageUrl, afterModel]);

    const handleDownloadAfterEdit = useCallback(async () => {
        setCompositeBusy(true);
        setLocalError(null);
        try {
            const dataUrl = await bakeEditedAfter();
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = `${patientName || "Patient"}-${smileType || "smile"}-edited-after.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err: unknown) {
            setLocalError(
                err instanceof Error ? err.message : "Could not download the edited after"
            );
        } finally {
            setCompositeBusy(false);
        }
    }, [bakeEditedAfter, patientName, smileType]);

    const handleViewAfterEdit = useCallback(async () => {
        setLocalError(null);
        try {
            await bakeEditedAfter();
            setEditedAfterViewOpen(true);
        } catch (err: unknown) {
            setLocalError(
                err instanceof Error ? err.message : "Could not open the edited after"
            );
        }
    }, [bakeEditedAfter]);

    const handleRefineAfter = useCallback(async () => {
        if (!imageUrl || !user?.user_id) return;
        setLocalError(null);
        setAiAction("refine");
        onExtendGenerateStart?.(imageUrl);
        try {
            const src = await bakeEditedAfter();
            const file = await urlToImageFile(
                src,
                `${patientName || "Patient"}-${smileType || "smile"}-edited-after.png`
            );
            const result = await dispatch(
                generateSmile({
                    user_id: String(user.user_id),
                    file,
                    customSmile: DENTAL_ACCURACY_REFINE_PROMPT,
                    promptLabel: DENTAL_ACCURACY_REFINE_LABEL,
                    imageModel: "gpt-image-2",
                    requestKey: smileId || "new",
                })
            ).unwrap();
            if (result?.success) {
                setAfterEditOpen(false);
                onComplete();
            }
        } catch (err: unknown) {
            const message =
                typeof err === "string"
                    ? err
                    : err instanceof Error
                      ? err.message
                      : "Refine failed";
            setLocalError(message);
            setAiAction(null);
        }
    }, [
        imageUrl,
        user?.user_id,
        bakeEditedAfter,
        patientName,
        smileType,
        dispatch,
        onComplete,
        onExtendGenerateStart,
        smileId,
    ]);

    const handleSaveAfterEdit = useCallback(async () => {
        if (!imageUrl || !afterModel) return;
        setCompositeBusy(true);
        setLocalError(null);
        try {
            const dataUrl = await bakeEditedAfter();
            const saved = await onSaveEditedOverlay?.(dataUrl);
            if (saved === false) {
                setLocalError("Could not save the edited after");
                return;
            }
            setAfterEdited(false);
            setAfterEditOpen(false);
        } catch (err: unknown) {
            setLocalError(
                err instanceof Error ? err.message : "Could not save the edited after"
            );
        } finally {
            setCompositeBusy(false);
        }
    }, [afterModel, bakeEditedAfter, imageUrl, onSaveEditedOverlay]);

    const canRefine =
        !!imageUrl &&
        !!user?.user_id &&
        !!afterModel &&
        !aiLoading &&
        !afterBusy &&
        !compositeBusy;

    const canSaveOutline = !!onSave && canSave;
    const hasUnsavedAfterComposite = afterEdited || !overlaySaved;

    const canSaveComposite =
        !!onSaveEditedOverlay &&
        canSaveOverlay &&
        !saving &&
        !compositeBusy &&
        !!afterModel &&
        hasUnsavedAfterComposite;

    const handleDownloadOverlay = useCallback(async () => {
        if (!overlayUrl) return;
        try {
            const response = await fetch(overlayUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const base = `${patientName || "Patient"}-${smileType || "smile"}-edited-after`;
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${base}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download edited on-before failed", err);
        }
    }, [overlayUrl, patientName, smileType]);

    const handleDownloadRestored = useCallback(async () => {
        if (!restoredUrl) return;
        try {
            const response = await fetch(restoredUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const base = `${patientName || "Patient"}-${smileType || "smile"}-refined`;
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${base}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download restored smile failed", err);
        }
    }, [restoredUrl, patientName, smileType]);

    const error = localError || outlineError;
    const refineLoading = aiLoading && aiAction === "refine";
    const editedPreviewUrl = overlayUrl || editedAfterUrl;

    const dentalSteps = [
        { id: 1, label: "Approved Library" },
        { id: 2, label: "edit on after" },
        { id: 3, label: "edited after" },
        { id: 4, label: "Refine" },
        { id: 5, label: "Save" },
    ] as const;

    const dentalStepCopy: Record<number, { title: string; description: string }> = {
        1: {
            title: "Choose an approved smile",
            description:
                "Select a generated after smile from Stage 1 to refine with dental accuracy tools.",
        },
        2: {
            title: "Edit teeth on after",
            description:
                "Open the after smile in the editor and change each tooth's width and height on the photo.",
        },
        3: {
            title: "View or download",
            description:
                "Check the edited after, download it as-is, or send it for a clean pass.",
        },
        4: {
            title: "Refine",
            description:
                "Clean seams and lighting only. Tooth sizes and positions you set are kept.",
        },
        5: {
            title: "Save dental assets",
            description:
                "Save the edited after and the refined image to this case.",
        },
    };

    const dentalCompleted = new Set<number>();
    if (imageUrl) dentalCompleted.add(1);
    if (afterModel || wireframeUrl) dentalCompleted.add(2);
    if (afterEdited || editedAfterUrl || overlayUrl) dentalCompleted.add(3);
    if (restoredUrl) dentalCompleted.add(4);
    if (
        (wireframeUrl && (wireframeSaved || selectedSaved)) ||
        (overlayUrl && overlaySaved) ||
        (restoredUrl && restoredSaved)
    ) {
        dentalCompleted.add(5);
    }

    let dentalActive = 1;
    if (!imageUrl) dentalActive = 1;
    else if (!afterModel && !wireframeUrl) dentalActive = 2;
    else if (!afterEdited && !editedAfterUrl && !overlayUrl) dentalActive = 3;
    else if (!restoredUrl) dentalActive = 4;
    else dentalActive = 5;

    const pipelineSteps = dentalSteps.slice(0, 4);
    const pipelineActive = Math.min(dentalActive, 4);
    const dentalHeader = dentalStepCopy[dentalActive];

    return (
        <div className="space-y-4 sm:space-y-6">
            <GenerationLoader loading={afterBusy} variant="dental-editor" />

            <StageStepsBar
                steps={[...dentalSteps]}
                activeStep={dentalActive}
                completedSteps={dentalCompleted}
            />

            <StageStepHeader
                title={dentalHeader.title}
                description={dentalHeader.description}
            />

            {!imageUrl && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
                    <FiTarget className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-300/90">
                        Select a generated after smile from Stage 1.
                    </p>
                </div>
            )}

            {error && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-500/5 border border-red-500/15">
                    <FiTarget className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300/90">{error}</p>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-6">
                <div className="xl:col-span-3 space-y-4 min-w-0">
                    <div className="rounded-2xl sm:rounded-3xl border border-[#2fa9bf]/20 bg-[#303030] p-3 sm:p-6 shadow-[0_0_30px_rgba(47,169,191,0.08)] space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h3 className="text-lg sm:text-xl font-bold flex items-center flex-wrap gap-x-1 gap-y-1">
                                    {pipelineSteps.map((step, index) => {
                                        const isActive = pipelineActive === step.id;
                                        return (
                                            <Fragment key={step.id}>
                                                <span
                                                    className={
                                                        isActive
                                                            ? "text-[#1DA8C6]"
                                                            : "text-[#6B6C72]"
                                                    }
                                                >
                                                    {step.label}
                                                </span>
                                                {index < pipelineSteps.length - 1 && (
                                                    <FiChevronRight
                                                        className={`w-5 h-5 shrink-0 ${
                                                            isActive
                                                                ? "ss-stepper-arrow-active text-[#1DA8C6] stroke-[3]"
                                                                : "text-[#6B6C72]"
                                                        }`}
                                                        aria-hidden
                                                    />
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </h3>
                                <p className="text-xs sm:text-sm text-[#8b8f8d] mt-0.5">
                                    {smileType
                                        ? smileType
                                        : "No after selected"}
                                    {afterModel ? " · editing teeth" : ""}
                                    {overlayUrl
                                        ? overlaySaved
                                            ? " · edited after saved"
                                            : " · edited after ready"
                                        : ""}
                                    {restoredUrl
                                        ? restoredSaved
                                            ? " · refine saved"
                                            : " · refine ready"
                                        : ""}
                                </p>
                            </div>
                            {patientName && (
                                <span className="px-3 py-1 rounded-full text-xs bg-[#101211] border border-white/5 text-gray-400 self-start">
                                    {patientName}
                                </span>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="rounded-xl overflow-hidden border border-white/5 bg-[#101211]">
                                <p className="text-sm uppercase tracking-wider text-[#8b8f8d] font-semibold px-3 pt-2.5">
                                    Approved Library
                                </p>
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt="Selected after smile"
                                        className="w-full max-h-[280px] object-contain mx-auto"
                                    />
                                ) : (
                                    <div className="h-[160px] flex items-center justify-center text-sm text-gray-500">
                                        No after selected
                                    </div>
                                )}
                            </div>
                            <div className="rounded-xl overflow-hidden border border-emerald-500/25 bg-[#101211]">
                                <p className="text-sm uppercase tracking-wider text-emerald-400/90 font-semibold px-3 pt-2.5">
                                    Edited after
                                </p>
                                {editedPreviewUrl ? (
                                    <div className="group relative">
                                        <img
                                            src={editedPreviewUrl}
                                            alt={
                                                overlayUrl
                                                    ? "Saved edited after"
                                                    : "Edited after smile"
                                            }
                                            className="w-full max-h-[280px] object-contain mx-auto"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/45 group-hover:opacity-100 transition-all">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    overlayUrl
                                                        ? setOverlayViewOpen(true)
                                                        : void handleViewAfterEdit()
                                                }
                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                            >
                                                <FiMaximize2 className="w-3.5 h-3.5" />
                                                View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    overlayUrl
                                                        ? void handleDownloadOverlay()
                                                        : void handleDownloadAfterEdit()
                                                }
                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/90 text-[#08151A] hover:bg-white"
                                            >
                                                <FiDownload className="w-3.5 h-3.5" />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-[160px] flex items-center justify-center text-sm text-gray-500 px-4 text-center">
                                        No edited after yet
                                    </div>
                                )}
                            </div>
                            <div className="rounded-xl overflow-hidden border border-violet-500/25 bg-[#101211]">
                                <p className="text-sm uppercase tracking-wider text-violet-300/90 font-semibold px-3 pt-2.5">
                                    Refine
                                </p>
                                {restoredUrl ? (
                                    <div className="group relative">
                                        <img
                                            src={restoredUrl}
                                            alt="Cleaned after smile"
                                            className="w-full max-h-[280px] object-contain mx-auto"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/45 group-hover:opacity-100 transition-all">
                                            <button
                                                type="button"
                                                onClick={() => setRestoredViewOpen(true)}
                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                            >
                                                <FiMaximize2 className="w-3.5 h-3.5" />
                                                View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void handleDownloadRestored()}
                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/90 text-[#08151A] hover:bg-white"
                                            >
                                                <FiDownload className="w-3.5 h-3.5" />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-[160px] flex items-center justify-center text-sm text-gray-500 px-4 text-center">
                                        {refineLoading ? "Refining…" : "No refine yet"}
                                    </div>
                                )}
                            </div>
                        </div>

                        {afterModel && (
                            <div className="rounded-xl border border-[#2fa9bf]/25 bg-[#101211] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                                <div className="min-w-0">
                                    <p className="text-sm uppercase tracking-wider text-[#2fa9bf] font-semibold">
                                        Teeth on after
                                    </p>
                                    <p className="text-xs text-[#8b8f8d] mt-0.5 truncate">
                                        {afterModel.teeth.length} teeth
                                        {afterEdited ? " · edited" : " · live editor"}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() => setAfterEditOpen(true)}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                    >
                                        <FiEdit3 className="w-3.5 h-3.5" />
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleViewAfterEdit()}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-white/10 text-gray-300 hover:border-[#2fa9bf]/40"
                                    >
                                        <FiMaximize2 className="w-3.5 h-3.5" />
                                        View
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadAfterEdit()}
                                        disabled={compositeBusy}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-white/10 text-gray-300 hover:border-[#2fa9bf]/40 disabled:opacity-40"
                                    >
                                        <FiDownload className="w-3.5 h-3.5" />
                                        {compositeBusy ? "Building…" : "Download"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleRefineAfter()}
                                        disabled={!canRefine}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-violet-500/90 text-[#08151A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {refineLoading ? "Refining…" : "Refine"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleSaveAfterEdit()}
                                        disabled={!canSaveComposite}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-500/90 text-[#08151A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <FiSave className="w-3.5 h-3.5" />
                                        {saving || compositeBusy
                                            ? "Saving…"
                                            : hasUnsavedAfterComposite
                                              ? "Save"
                                              : "Saved"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetAfterTeeth}
                                        className="px-3 py-2 rounded-lg text-xs text-gray-500 hover:text-gray-300"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                <div className="xl:col-span-2 space-y-4">
                    <FormCard title="Controls">
                        <div className="space-y-3">
                            <ActionButton
                                onClick={() => void handleEditOnAfter()}
                                loading={afterBusy}
                                disabled={!imageUrl || afterBusy}
                                text={
                                    afterModel
                                        ? "Open after editor"
                                        : "Edit teeth on after"
                                }
                                loadingText="Opening editor…"
                            />

                            <ActionButton
                                onClick={() => void handleRefineAfter()}
                                loading={refineLoading}
                                disabled={!canRefine}
                                text="Refine"
                                loadingText="Cleaning…"
                            />

                            <button
                                type="button"
                                onClick={() => void handleViewAfterEdit()}
                                disabled={!afterModel && !editedAfterUrl}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#2fa9bf]/35 bg-[#2fa9bf]/10 text-[#2fa9bf] text-sm font-semibold hover:bg-[#2fa9bf]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FiMaximize2 className="w-4 h-4" />
                                View edited after
                            </button>

                            <button
                                type="button"
                                onClick={() => void handleDownloadAfterEdit()}
                                disabled={(!afterModel && !editedAfterUrl) || compositeBusy}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#2fa9bf]/35 bg-[#2fa9bf]/10 text-[#2fa9bf] text-sm font-semibold hover:bg-[#2fa9bf]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FiDownload className="w-4 h-4" />
                                {compositeBusy ? "Building…" : "Download edited after"}
                            </button>

                            <ActionButton
                                onClick={() => onSave?.()}
                                loading={saving}
                                disabled={!canSaveOutline || saving || aiLoading}
                                text={
                                    canSaveOutline
                                        ? "Save"
                                        : "Saved"
                                }
                                loadingText="Saving…"
                                icon="save"
                                variant={canSaveOutline ? "primary" : "ghost"}
                            />

                            <button
                                type="button"
                                onClick={() => void handleDownloadRestored()}
                                disabled={!restoredUrl}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#2fa9bf]/35 bg-[#2fa9bf]/10 text-[#2fa9bf] text-sm font-semibold hover:bg-[#2fa9bf]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download refine
                            </button>

                            {overlayUrl && overlaySaved && (
                                <p className="text-xs text-emerald-400/90 text-center flex items-center justify-center gap-1.5">
                                    <FiCheckCircle className="w-3 h-3" />
                                    Edited after saved
                                </p>
                            )}

                            {restoredUrl && restoredSaved && (
                                <p className="text-xs text-emerald-400/90 text-center flex items-center justify-center gap-1.5">
                                    <FiCheckCircle className="w-3 h-3" />
                                    Refine saved
                                </p>
                            )}

                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#101211] border border-white/5">
                                <span
                                    className={`w-2 h-2 rounded-full shrink-0 ${
                                        aiLoading || afterBusy
                                            ? "bg-[#2fa9bf] animate-pulse"
                                            : restoredUrl || afterModel
                                              ? "bg-emerald-400"
                                              : imageUrl
                                                ? "bg-amber-400"
                                                : "bg-gray-500"
                                    }`}
                                />
                                <span className="text-xs text-[#8b8f8d]">
                                    {refineLoading
                                        ? "Refining after…"
                                        : afterBusy
                                          ? "Opening editor…"
                                          : aiLoading
                                            ? "Generating…"
                                            : restoredUrl
                                              ? "Refine ready"
                                              : afterModel
                                                ? "After editor ready — view, download, or refine"
                                                : !imageUrl
                                                  ? "Select an after"
                                                  : "Ready"}
                                </span>
                            </div>
                        </div>
                    </FormCard>
                </div>
            </div>

            <SmileSuperimposeViewer
                beforeSrc={beforePreview}
                approvedLibrarySrc={imageUrl}
                editedAfterSrc={editedPreviewUrl}
                refineSrc={restoredUrl}
                patientName={patientName}
                smileType={smileType}
            />

            {afterModel && imageUrl && (
                <AfterToothEditView
                    open={afterEditOpen}
                    onClose={() => setAfterEditOpen(false)}
                    afterSrc={imageUrl}
                    beforeSrc={beforePreview}
                    smileType={smileType}
                    model={afterModel}
                    selectedId={afterSelectedId}
                    selectedIds={afterSelectedIds}
                    selectedHandle={afterSelectedHandle}
                    onSelect={(id, handle, mode) => {
                        setAfterSelectedHandle(mode === "toggle" ? null : handle);
                        if (!id) {
                            setAfterSelectedId(null);
                            setAfterSelectedIds([]);
                            return;
                        }
                        if (mode === "focus") {
                            setAfterSelectedId(id);
                            setAfterSelectedIds((prev) =>
                                prev.includes(id) ? prev : [id]
                            );
                            return;
                        }
                        if (mode === "toggle") {
                            const base = afterSelectedIds.length
                                ? afterSelectedIds
                                : afterSelectedId
                                  ? [afterSelectedId]
                                  : [];
                            const has = base.includes(id);
                            const next = has
                                ? base.filter((x) => x !== id)
                                : [...base, id];
                            const primary = has
                                ? afterSelectedId === id
                                    ? (next[next.length - 1] ?? null)
                                    : afterSelectedId
                                : id;
                            setAfterSelectedIds(next);
                            setAfterSelectedId(primary);
                            return;
                        }
                        setAfterSelectedId(id);
                        setAfterSelectedIds([id]);
                    }}
                    onToothChange={(tooth) => {
                        setAfterModel((prev) => {
                            if (!prev) return prev;
                            const before = prev.teeth.find((t) => t.id === tooth.id);
                            const patch: { scaleX?: number; scaleY?: number } = {};
                            if (
                                before &&
                                (tooth.scaleX ?? 1) !== (before.scaleX ?? 1)
                            ) {
                                patch.scaleX =
                                    (tooth.scaleX ?? 1) - (before.scaleX ?? 1);
                            }
                            if (
                                before &&
                                (tooth.scaleY ?? 1) !== (before.scaleY ?? 1)
                            ) {
                                patch.scaleY =
                                    (tooth.scaleY ?? 1) - (before.scaleY ?? 1);
                            }
                            const share =
                                (patch.scaleX != null || patch.scaleY != null) &&
                                afterSelectedIds.includes(tooth.id)
                                    ? new Set(afterSelectedIds)
                                    : null;
                            return {
                                ...prev,
                                teeth: prev.teeth.map((t) => {
                                    if (t.id === tooth.id) return tooth;
                                    if (!share?.has(t.id)) return t;
                                    return setWireToothTransform(t, {
                                        ...(patch.scaleX != null
                                            ? { scaleX: (t.scaleX ?? 1) + patch.scaleX }
                                            : {}),
                                        ...(patch.scaleY != null
                                            ? { scaleY: (t.scaleY ?? 1) + patch.scaleY }
                                            : {}),
                                    });
                                }),
                            };
                        });
                        setAfterEdited(true);
                    }}
                    onResetAll={() => {
                        if (afterOriginal) setAfterModel(cloneTeeth(afterOriginal));
                        setAfterEdited(false);
                        setAfterSelectedHandle(null);
                    }}
                    edited={afterEdited}
                    onDownload={() => void handleDownloadAfterEdit()}
                    onView={() => void handleViewAfterEdit()}
                    onRefine={() => void handleRefineAfter()}
                    refineBusy={refineLoading}
                    canRefine={canRefine}
                    onSave={() => void handleSaveAfterEdit()}
                    actionBusy={compositeBusy}
                    canSave={canSaveComposite}
                    saving={saving}
                />
            )}

            {editedAfterViewOpen &&
                editedAfterUrl &&
                createPortal(
                    <div className="fixed inset-0 z-[300] flex flex-col bg-[#0a0b0c]/96 backdrop-blur-sm">
                        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-[#16171a]">
                            <div className="min-w-0">
                                <p className="text-[#2fa9bf] text-xs font-semibold uppercase tracking-wider">
                                    Edited after
                                </p>
                                <p className="text-sm text-gray-400 truncate mt-0.5">
                                    {afterEdited ? "Customized" : "Detected"} ·{" "}
                                    {smileType || "this after"}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => void handleDownloadAfterEdit()}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                >
                                    <FiDownload className="w-3.5 h-3.5" />
                                    Download
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleRefineAfter()}
                                    disabled={!canRefine}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-violet-500/90 text-[#08151A] hover:brightness-110 disabled:opacity-40"
                                >
                                    {refineLoading ? "Refining…" : "Refine"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditedAfterViewOpen(false)}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                                    aria-label="Close viewer"
                                >
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div
                            className="flex-1 min-h-0 overflow-auto p-4 sm:p-6 flex items-center justify-center"
                            onClick={() => setEditedAfterViewOpen(false)}
                        >
                            <img
                                src={editedAfterUrl}
                                alt="Edited after smile"
                                className="max-w-full max-h-full object-contain rounded-lg border border-white/10 shadow-2xl"
                                draggable={false}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>,
                    document.body
                )}

            {overlayViewOpen &&
                overlayUrl &&
                createPortal(
                    <div className="fixed inset-0 z-[300] flex flex-col bg-[#0a0b0c]/96 backdrop-blur-sm">
                        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-[#16171a]">
                            <div className="min-w-0">
                                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">
                                    Edited after
                                </p>
                                <p className="text-sm text-gray-400 truncate mt-0.5">
                                    {overlaySaved ? "Saved" : "Ready"} · linked to{" "}
                                    {smileType || "this after"}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => void handleDownloadOverlay()}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                >
                                    <FiDownload className="w-3.5 h-3.5" />
                                    Download
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setOverlayViewOpen(false)}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                                    aria-label="Close viewer"
                                >
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div
                            className="flex-1 min-h-0 overflow-auto p-4 sm:p-6 flex items-center justify-center"
                            onClick={() => setOverlayViewOpen(false)}
                        >
                            <img
                                src={overlayUrl}
                                alt="Edited after"
                                className="max-w-full max-h-full object-contain rounded-lg border border-white/10 shadow-2xl"
                                draggable={false}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>,
                    document.body
                )}

            {restoredViewOpen &&
                restoredUrl &&
                createPortal(
                    <div className="fixed inset-0 z-[300] flex flex-col bg-[#0a0b0c]/96 backdrop-blur-sm">
                        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-[#16171a]">
                            <div className="min-w-0">
                                <p className="text-violet-300 text-xs font-semibold uppercase tracking-wider">
                                    Refine
                                </p>
                                <p className="text-sm text-gray-400 truncate mt-0.5">
                                    {restoredSaved ? "Saved" : "Ready"} · linked to{" "}
                                    {smileType || "this after"}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => void handleDownloadRestored()}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#2fa9bf] text-[#08151A] hover:brightness-110"
                                >
                                    <FiDownload className="w-3.5 h-3.5" />
                                    Download
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRestoredViewOpen(false)}
                                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                                    aria-label="Close viewer"
                                >
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div
                            className="flex-1 min-h-0 overflow-auto p-4 sm:p-6 flex items-center justify-center"
                            onClick={() => setRestoredViewOpen(false)}
                        >
                            <img
                                src={restoredUrl}
                                alt="Refined smile"
                                className="max-w-full max-h-full object-contain rounded-lg border border-white/10 shadow-2xl"
                                draggable={false}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
